import { Box, Group, Loader, Paper, Stack, Text, VisuallyHidden } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useAppSelector } from "../app/hooks";
import { selectDrofusChangesFilterSummary } from "./drofusChangesSelectors";

const numberFormatter = new Intl.NumberFormat("nb-NO");

interface IAnimatedChangeCountsProps {
  changeCount: number;
  roomCount: number;
}

const AnimatedChangeCounts: React.FC<IAnimatedChangeCountsProps> = ({ changeCount, roomCount }) => {
  const reducedMotion = useReducedMotion();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (reducedMotion) return;
    setProgress(0);
    let frame: number;
    let startedAt: number | undefined;
    const animate = (time: number) => {
      startedAt ??= time;
      const elapsed = Math.min((time - startedAt) / 450, 1);
      setProgress(1 - Math.pow(1 - elapsed, 3));
      if (elapsed < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [changeCount, roomCount, reducedMotion]);

  const fraction = reducedMotion ? 1 : progress;
  return (
    <Group gap="lg" align="flex-end" aria-hidden="true">
      <Box>
        <Text fz={30} fw={750} lh={1.15} c="blue.9" style={{ fontVariantNumeric: "tabular-nums" }}>
          {numberFormatter.format(Math.round(changeCount * fraction))}
        </Text>
        <Text size="xs" c="blue.9">
          feltendringer
        </Text>
      </Box>
      <Box pl="md" style={{ borderLeft: "1px solid var(--mantine-color-blue-2)" }}>
        <Text fz={24} fw={700} lh={1.25} c="blue.9" style={{ fontVariantNumeric: "tabular-nums" }}>
          {numberFormatter.format(Math.round(roomCount * fraction))}
        </Text>
        <Text size="xs" c="blue.9">
          rom
        </Text>
      </Box>
    </Group>
  );
};

export const DrofusChangesFilterSummary: React.FC = () => {
  const summary = useAppSelector(selectDrofusChangesFilterSummary);
  const status = summary.isLoading
    ? "Oppdaterer treff…"
    : summary.isError
      ? "Kunne ikke hente treff."
      : `${numberFormatter.format(summary.changeCount)} feltendringer i ${numberFormatter.format(summary.roomCount)} rom på valgt etasje.`;

  return (
    <Paper
      p="sm"
      radius="md"
      bg="blue.0"
      style={{ border: "1px solid var(--mantine-color-blue-2)" }}
    >
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {status}
      </VisuallyHidden>
      <Stack gap="xs">
        <Text size="xs" fw={600} c="blue.8">
          Treff på valgt etasje
        </Text>
        <Box mih={50}>
          {summary.isLoading ? (
            <Group gap="xs" mih={50} aria-hidden="true">
              <Loader size="sm" />
              <Text size="sm" c="blue.9">
                Oppdaterer treff…
              </Text>
            </Group>
          ) : summary.isError ? (
            <Text size="sm" c="red.8" aria-hidden="true">
              Kunne ikke hente treff.
            </Text>
          ) : (
            <AnimatedChangeCounts changeCount={summary.changeCount} roomCount={summary.roomCount} />
          )}
        </Box>
      </Stack>
    </Paper>
  );
};
